import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The Android install affordance (AC-1).
 *
 * Chrome fires `beforeinstallprompt` when the app meets the installability bar
 * (valid manifest with name + 192/512 icons, a registered service worker,
 * served over HTTPS). We capture the event, `preventDefault()` it so Chrome does
 * not show its own mini-infobar, and surface OUR affordance instead. Tapping it
 * calls `prompt()` on the saved event — the native install dialog.
 *
 * The affordance appears ONLY once `beforeinstallprompt` has fired: never a dead
 * "Install" control on a browser or platform that cannot install (CLAUDE.md — no
 * control for a feature that does not exist here yet). On iOS Safari the event
 * never fires, so `canInstall` stays false and nothing is shown; iOS install is
 * the manual Share → Add to Home Screen, which needs no control from us.
 *
 * The `beforeinstallprompt` type is not in the DOM lib; the minimal shape is
 * declared locally rather than casting through `any`.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export interface PwaInstall {
  /** True once the browser has offered an install (event captured). */
  canInstall: boolean;
  /** Show the native install dialog. No-op if nothing was captured. */
  promptInstall: () => Promise<void>;
}

export function usePwaInstall(): PwaInstall {
  const deferred = useRef<BeforeInstallPromptEvent | null>(null);
  const [canInstall, setCanInstall] = useState(false);

  useEffect(() => {
    const onBeforeInstall = (event: Event) => {
      event.preventDefault(); // suppress Chrome's own mini-infobar
      deferred.current = event as BeforeInstallPromptEvent;
      setCanInstall(true);
    };
    const onInstalled = () => {
      // Installed — the affordance has done its job and must disappear.
      deferred.current = null;
      setCanInstall(false);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    const event = deferred.current;
    if (!event) return;
    await event.prompt();
    // The event is single-use; whatever the user chose, it cannot be reused.
    deferred.current = null;
    setCanInstall(false);
  }, []);

  return { canInstall, promptInstall };
}
